import { usFillProgress, usOrderNeedsFillVisibility, type UsInventoryFill } from '../../lib/polymarketUs/fillProgress';
/**
 * #1034 — what the side rail reads for Polymarket US.
 *
 * The rail shows the trader's OWN open US orders in every US account they can
 * view as their own — the same scope as the Tracker's Cancel all (#1006):
 * teammates' orders on a shared account are the desk view's, not the rail's.
 *
 * Balances (#1034) are a separate read, `readUsRailBalances`, run by the hook
 * in its own lane after the orders are shown, so a slow balance never delays
 * the orders, a post-cancel re-read or the Cancel all count. Only where the
 * cash is the caller's to see: the personal owner's, or a shared account the
 * caller has desk view of. Balances are per account, never added together.
 *
 * One read walks the visible accounts one at a time. A failure in one account
 * is kept on that account (its rows are unknown, never empty) and does not
 * hide the others; only a failed account list fails the whole read. Uses the
 * caller's own client instances, so it never disturbs the Tracker's US
 * section or a Cancel all read.
 */
import type { UsAccountClient } from '../../lib/polymarketUs/accountClient';
import type { UsPortfolioClient, UsSavedBalance } from '../../lib/polymarketUs/portfolioClient';
import { presentUsOrder, usCancelView } from '../../lib/polymarketUs/portfolioModel';
import type { UsOrderRow, UsPositionRow, UsReadiness, UsScope } from '../../lib/polymarketUs/types';
import { isKnownZero, readinessOf, US_BULK_MAX_ACCOUNT_PAGES, US_BULK_MAX_ORDER_PAGES } from '../PositionTracker/usBulkCancelModel';

export type UsRailClients = {
  accounts: Pick<UsAccountClient, 'listAccounts' | 'selectAccount'>;
  portfolio: Pick<UsPortfolioClient, 'setSelection' | 'readOrderPage'>;
};

/** The account's balance as the rail can show it (#1034). Like K/P, the
 * venue-reported cash with no hold math. A shared account's cash belongs to
 * the desk: a member who views only their own orders is not shown it (the
 * relay would refuse it too), so it is never read for them. */
export type UsRailBalance =
  | { state: 'saved'; balance: UsSavedBalance }
  | { state: 'none' }
  | { state: 'shared' }
  /** Not read yet: the orders read leaves it for `readUsRailBalances`. */
  | { state: 'pending' }
  /** `last`: the reading shown before this refresh failed (kept, as K/P keep
   * their last figure); set by the rail hook, never across a login. */
  | { state: 'unavailable'; error: string; last?: UsSavedBalance };

export type UsRailAccount = {
  accountId: string;
  /** Names the account beside its balance (a trader may hold several). */
  ownership: 'personal' | 'pooled';
  balance: UsRailBalance;
  /** Null when the account could not be read. */
  scope: UsScope | null;
  readiness: UsReadiness | null;
  /** Own orders that are not finished (open, partial, pending or uncertain). */
  orders: readonly UsOrderRow[];
  /** More saved orders than one read covers; the rest were not read. */
  ordersCapped: boolean;
  /** The trader's holdings in this account, except ones known to be zero.
   * A holding whose size is unknown stays (its size is shown as unknown). */
  positions: readonly UsPositionRow[];
  /** Why this account's holdings could not be read; null when they were. */
  positionsError: string | null;
  /** Explicit inventory coverage from the existing first-page read. Older
   * snapshots without coverage remain unknown for aggregate economics. */
  positionsComplete?: boolean;
  /** Why this account's rows are unknown; null when it was read. */
  error: string | null;
  /** Set only on a failed account (`error` set) that shows the rows it last
   * showed: when they were read (ms since the epoch). They are not current,
   * and the account stays unknown for counts. Set by the rail hook. */
  carriedFromMs?: number;
};

export type UsRailRead = { accounts: readonly UsRailAccount[] };

const code = (error: unknown) => error && typeof error === 'object' && 'code' in error
  && typeof error.code === 'string' ? error.code : 'US_READ_UNAVAILABLE';

const contextChanged = () => Object.assign(new Error('US_CLIENT_CONTEXT_CHANGED'), { code: 'US_CLIENT_CONTEXT_CHANGED' });

/**
 * Read every visible own account's orders and holdings. `stillCurrent` is
 * checked after each await; a login or org change aborts the whole read so
 * nothing from it is shown. Balances come back `pending` (or `shared`).
 */
export async function readUsRail(clients: UsRailClients, stillCurrent: () => boolean): Promise<UsRailRead> {
  const guard = () => { if (!stillCurrent()) throw contextChanged(); };
  const listed: { accountId: string; ownership: 'personal' | 'pooled'; cashVisible: boolean }[] = [];
  let after: string | undefined;
  for (let page = 0; ; page++) {
    const result = await clients.accounts.listAccounts(after);
    guard();
    // Cash is account-wide: the personal owner's, or the desk's on a shared account.
    for (const a of result.accounts) {
      if (a.canViewOwn) listed.push({ accountId: a.accountId, ownership: a.ownership, cashVisible: a.ownership === 'personal' || a.canViewDesk });
    }
    if (!result.nextCursor) break;
    if (page + 1 >= US_BULK_MAX_ACCOUNT_PAGES) throw Object.assign(new Error('US_RAIL_TOO_MANY_ACCOUNTS'), { code: 'US_RAIL_TOO_MANY_ACCOUNTS' });
    after = result.nextCursor;
  }

  const accounts: UsRailAccount[] = [];
  try {
    for (const { accountId, ownership, cashVisible } of listed) {
      try {
        const context = await clients.accounts.selectAccount(accountId, 'own');
        guard();
        clients.portfolio.setSelection({ accountId: context.accountId, ...context.scope, view: 'own' });
        const orders: UsOrderRow[] = [];
        let positions: readonly UsPositionRow[] = [];
        let holdingEvidence: readonly UsPositionRow[] = [];
        let orderFills: readonly UsInventoryFill[] | undefined;
        let positionsError: string | null = null;
        let positionsComplete = false;
        const seen = new Set<string>();
        let ordersCapped = false;
        let cursor: string | undefined;
        for (let page = 0; ; page++) {
          const snapshot = await clients.portfolio.readOrderPage(context.scope, cursor);
          guard();
          if (page === 0) {
            // Every page carries the same holdings read; the first one is used.
            const inventory = snapshot.readDetails?.inventory;
            positionsError = inventory?.state === 'unavailable' ? inventory.reason : null;
            positionsComplete = inventory?.state === 'complete';
            holdingEvidence = snapshot.positions;
            orderFills = snapshot.readDetails?.orderFills;
            positions = snapshot.positions.filter((p) => !isKnownZero(p.heldQuantity));
          }
          for (const row of snapshot.orders) {
            // A row repeated across saved pages is one order, shown once.
            // Later order pages may contain a fill newer than the holdings we
            // actually show. Only that first holdings snapshot can retire it.
            const shown = orderFills === undefined ? row : { ...row, fillProgress: usFillProgress(row, orderFills,
              holdingEvidence.some(p => p.target.marketSlug === row.target.marketSlug && p.target.outcome === row.target.outcome
                && p.heldQuantity !== null && !p.reasons.length)) };
            if (shown.ownership !== 'own' || (presentUsOrder(shown).terminal && !usOrderNeedsFillVisibility(shown)) || seen.has(shown.intentId)) continue;
            seen.add(shown.intentId);
            orders.push(shown);
          }
          const next = snapshot.readDetails?.nextOrderCursor ?? null;
          if (!next) break;
          if (page + 1 >= US_BULK_MAX_ORDER_PAGES) { ordersCapped = true; break; }
          cursor = next;
        }
        accounts.push({ accountId, ownership, balance: cashVisible ? { state: 'pending' } : { state: 'shared' }, scope: context.scope,
          readiness: readinessOf(context), orders, ordersCapped, positions, positionsError, positionsComplete, error: null });
      } catch (error) {
        if (code(error) === 'US_CLIENT_CONTEXT_CHANGED' && !stillCurrent()) throw error;
        accounts.push({ accountId, ownership, balance: cashVisible ? { state: 'unavailable', error: code(error) } : { state: 'shared' },
          scope: null, readiness: null, orders: [], ordersCapped: false,
          positions: [], positionsError: null, error: code(error) });
      }
    }
  } finally {
    clients.portfolio.setSelection(null);
  }
  return { accounts };
}

/**
 * #1034 — each pending balance in `read`, one account at a time, on the
 * caller's own client (never the orders read's). A failure stays on its
 * account; a login change aborts the whole read.
 */
export async function readUsRailBalances(portfolio: Pick<UsPortfolioClient, 'setSelection' | 'readBalance'>,
  read: UsRailRead, stillCurrent: () => boolean): Promise<Map<string, UsRailBalance>> {
  const out = new Map<string, UsRailBalance>();
  try {
    for (const a of read.accounts) {
      if (a.balance.state !== 'pending' || !a.scope) continue;
      try {
        portfolio.setSelection({ accountId: a.accountId, ...a.scope, view: 'own' });
        const saved = await portfolio.readBalance(a.scope);
        if (!stillCurrent()) throw contextChanged();
        out.set(a.accountId, saved ? { state: 'saved', balance: saved } : { state: 'none' });
      } catch (error) {
        if (!stillCurrent()) throw contextChanged();
        out.set(a.accountId, { state: 'unavailable', error: code(error) });
      }
    }
  } finally {
    portfolio.setSelection(null);
  }
  return out;
}

/**
 * A failed account keeps the rows it last showed, so they don't vanish and
 * return each time one account's read fails and the next succeeds: its
 * previous orders, holdings, scope and readiness, stamped with when they were
 * read (`prevReadAtMs`: the time of `prev`, kept across repeated failures).
 * `error` stays set, so the account is still unknown: never "no orders", and
 * never counted (`usRailCancellableCount`). A carried row keeps its cancel
 * control; the relay re-checks every cancel. Only within one login: the caller
 * passes `prev` only when it was read under the same identity.
 */
export function carryUsRailFailedAccounts(next: UsRailRead, prev: UsRailRead | null, prevReadAtMs: number | null): UsRailRead {
  if (!prev) return next;
  const before = new Map(prev.accounts.map((a) => [a.accountId, a]));
  return { accounts: next.accounts.map((a) => {
    const p = before.get(a.accountId);
    if (a.error === null || !p || !p.scope) return a;
    const from = p.carriedFromMs ?? prevReadAtMs;
    if (from === null) return a;
    return { ...a, scope: p.scope, readiness: p.readiness, orders: p.orders, ordersCapped: p.ordersCapped,
      positions: p.positions, positionsError: p.positionsError, carriedFromMs: from };
  }) };
}

/** USD text → cents text, as the rail's K and P rows read ("0.425" → "42.5¢").
 * Moves the decimal point in the text itself, so nothing is rounded; text that
 * is not a plain decimal is shown as saved, in dollars. */
export function usRailPrice(outcomePrice: string): string {
  const m = /^(\d+)(?:\.(\d+))?$/.exec(outcomePrice);
  if (!m) return `$${outcomePrice}`;
  const fraction = (m[2] ?? '').padEnd(2, '0');
  const whole = `${m[1]}${fraction.slice(0, 2)}`.replace(/^0+(?=\d)/, '');
  const rest = fraction.slice(2).replace(/0+$/, '');
  return `${whole}${rest ? `.${rest}` : ''}¢`;
}

/** When the rail's rows were read, as local wall-clock HH:MM:SS (24-hour). */
export function usRailReadTime(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
}

/** Own US orders a Cancel all could send now, across every account read —
 * the same rule as its frozen targets (an exact venue id and the cancel
 * right). Null when no read has answered for this login. Only a first cancel
 * counts, which does not depend on the clock (as `usVisibleInventory(…, 0)`).
 * An account whose read failed is unknown, never a number, even while the
 * rail shows its last rows. */
export function usRailCancellableCount(read: UsRailRead | null): number | null {
  if (!read) return null;
  let n = 0;
  for (const account of read.accounts) {
    if (!account.readiness || account.error !== null) continue;
    for (const row of account.orders) if (row.orderId && usCancelView(row, account.readiness, 0).canCancel) n++;
  }
  return n;
}
